import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import DatePicker from "~/common/components/ui/DatePicker";
import {
  FieldsetCard,
  FieldsetCardProps,
  getDisabledClasses,
} from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import { getTodayDate } from "~/common/utils";
import { PRONOUNS } from "~/patients/constants/constants";
import { pronounsOptionsWithOther } from "~/patients/utils/getPronouns";
import { UpdatePatientDetailsForm } from "~/providers/types/patients";

export const PersonalDetailsFieldset = ({
  disabled,
  ...props
}: Omit<FieldsetCardProps, "title" | "className">) => {
  const {
    register,
    formState: { errors },
    control,
    watch,
    resetField,
  } = useFormContext<UpdatePatientDetailsForm>();

  const hasCustomPronouns = watch("pronouns.pronouns") === PRONOUNS.other;

  return (
    <FieldsetCard
      title="Personal Details"
      className="border-none p-0 shadow-none"
      contentClassName={twMerge("sm:grid-cols-2", disabled && "sm:grid-cols-4")}
      disabled={disabled}
      {...props}
    >
      <Input
        label="First name"
        {...getDisabledClasses(disabled)}
        {...register("name")}
        error={errors.name?.message}
        disabled={disabled}
      />
      <Input
        label="Last name"
        {...getDisabledClasses(disabled)}
        {...register("lastName")}
        error={errors.lastName?.message}
        disabled={disabled}
      />
      <Select
        id="pronouns.pronouns"
        error={errors?.pronouns?.message}
        label="Pronouns"
        placeholder={"Select pronouns"}
        options={pronounsOptionsWithOther}
        control={control}
        isDisabled={disabled}
        buttonClassName="disabled:bg-transparent disabled:border-none disabled:p-0 disabled:h-auto"
        onChange={() => resetField("pronouns.customPronouns")}
        icon={disabled ? <ChevronDownIcon /> : undefined}
      />
      {hasCustomPronouns && (
        <Input
          label="Custom pronouns"
          {...getDisabledClasses(disabled)}
          {...register("pronouns.customPronouns")}
          error={errors.pronouns?.customPronouns?.message}
          disabled={disabled}
        />
      )}
      <DatePicker
        label="Date of birth"
        max={getTodayDate()}
        {...register("dateOfBirth")}
        error={errors.dateOfBirth?.message}
        className={twMerge(
          getDisabledClasses(disabled).className,
          "border-gray-300",
        )}
        inputClassName={twMerge(
          getDisabledClasses(disabled).inputClassName,
          "disabled:text-gray-700",
        )}
      />
    </FieldsetCard>
  );
};
